import React, { FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject, Fragment } from 'react';

import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import InsightsIcon from '@mui/icons-material/Insights';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import MoreIcon from '@mui/icons-material/More';

import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';

import { useTheme } from '@mui/material/styles';

import { IEntity, IResult } from 'library/interface';

import useUtils from 'library/utils';

import useRegistrationMarkService from './services/RegistrationMark';

import { IRegistrationMark, ISchoolYearClassSubjectMark, ISchoolYearClassReportCard, defaultSchoolYearClassSubjectMark, defaultSchoolYearClassReportCard, ReportCardType, IMarkDashboard, defaultMarkDashboard, IRegistrationTypeMark } from './models/RegistrationMark';

import useEntityService from 'features/services/Entity';
import {
  currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom,
  isSearchBoxShowAtom, isSaveLoadingAtom
} from 'library/store';

import externalLibraryService from 'features/configuration/services/ExternalLibrary';
import { defaultExternalLibrary, defaultExternalLibraryClass, IExternalLibrary, IExternalLibraryClass } from 'features/configuration/models/ExternalLibrary';


import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { isFalsy } from 'utility-types';
import { FormDialog } from 'components/ui/FormDialog';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, Typography, ToggleButtonGroup, ToggleButton, Avatar } from '@mui/material';
import IEnumeration, {
  IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE, Enum_DAY_WEEK,
  Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_STUDY_SECTION, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_TEACHER_STATUS, Enum_STUDY_CYCLE,
} from 'features/configuration/models/Enumeration';

import useEnumerationService, { useBasicFilterEnumeration } from 'features/configuration/services/Enumeration';

import usePersonService from 'features/production/services/Person';

import { BasicTextFilterForm, defaultBasicTextFilterProps } from 'components/ui/BasicTextFilterForm';
import { ISchoolYearClass, ISchoolYearClassSubject, ISchoolYearClassSubjectExamSchedule, ISchoolYearClassTemplate, ISchoolYearPeriod, ISchoolYearPeriodExam, ISchoolYearTemplateSubjectExamSchedule } from 'features/setup/models/SchoolYear';
import useSchoolYearService, { useBasicFilterSchoolYearClass } from 'features/setup/services/SchoolYear';
import { DatePicker } from '@mui/x-date-pickers';
import { typographyGroupBoxStyling } from 'themes/commonStyles';

import { debounce, sum } from 'lodash';
import { defaultRegistration, IRegistration } from './models/Registration';
import EnhancedTable from 'components/ui/EnhancedTable';

import WidgetPieChart from 'features/widgets/WidgetPieChart';
//import { useBasicFilterApplicationQuery } from 'features/setup/services/ApplicationQuery';


export const MarkDashboardForm = () => {

  const theme = useTheme();

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { id } = useParams();

  const { language: lg, schoolYears, applicationSetup, roleEntities } = useRecoilValue(currentUserSessionAtom);

  const [_id, _setId] = useState<number>(Number(id || 0));

  const { generateDistinctColorFromName } = useUtils();

  const { openEntityPrintDrawer, openEntityPrintDrawerEx } = useEntityService();

  const { enqueueSnackbar } = useSnackbar();

  const { retrieveEntity, retrieveData, openEntityActionDrawer, checkEntitySaveAuthorization } = useEntityService();
  const { saveSchoolYearClassReportCard, getMarks } = useRegistrationMarkService();

  const registrationMarkServiceRef = useRef(useRegistrationMarkService());

  const { getPhysicalPersonNameLg } = usePersonService();

  const { getEnumerationItemsByEnumerationCodes, getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();

  // const { getSchoolYearClassTemplates, getSchoolYearClassRegistrationMarks, getSchoolYearClassSubjects,
  //   getSchoolYearPeriods, getSchoolYearPeriodExams, getSchoolYearTemplateSubjectExamSchedules, 
  //   getSchoolYearClasses } = useSchoolYearService();

  const schoolYearServiceRef = useRef(useSchoolYearService());

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  // const basicFilterApplicationQuery = useBasicFilterApplicationQuery( 

  //   async (event: React.MouseEvent<unknown>, row: IApplicationQuery) => {
  //     const {id, name } = row;

  //     setIsSearchBoxShow(false);
  //     _setId(row.id);
  //   }
  // );

  const [openClassFilter, setOpenClassFilter] = useState(false);
  const basicFilterSchoolYearClass = useBasicFilterSchoolYearClass(
    async (event: React.MouseEvent<unknown>, row: ISchoolYearClass) => {

      //const schoolYearClass = await retrieveEntity('SchoolYearClass',row.id) as ISchoolYearClass;
      const { id, studyType, name, schoolYearClassTemplateId,
        classTemplateName, studyLanguage,
        fullTeacherName, substituteTeacherName,
        schoolYearId
      } = row;

      const data = getValues();
      // reset({...data, schoolYearClassId: id, 
      //       schoolYearId, schoolYearClassTemplateId,

      //       schoolYearClassName: name, studyType, 
      //       fullTeacherName, substituteTeacherName, classTemplateName, studyLanguage,
      //      })

      setValue('schoolYearId', schoolYearId);

      const arr = await schoolYearServiceRef.current.getSchoolYearClassSubjects(id); // schoolYearClassId

      setSchoolYearClassSubjects([...arr]);

      setOpenClassFilter(false);
    }
  );


  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration(
    (event: React.MouseEvent<unknown>, row: IEnumeration) => {
      const { id, name, description } = row;


    }
  );





  const emptyFunc = (obj: any) => { }

  const methods = useForm<IMarkDashboard>({ defaultValues: defaultMarkDashboard });
  const { register, setValue, getValues, watch, reset, handleSubmit, setFocus, control, formState: { errors } } = methods;

  const watchSchoolYearId = watch('schoolYearId');
  const watchSchoolYearPeriodId = watch('schoolYearPeriodId');
  const watchSchoolYearPeriodExamId = watch('schoolYearPeriodExamId');

  const watchStudyType = watch('studyType');
  const watchStudySection = watch('studySection');
  const watchStudyLanguage = watch('studyLanguage');

  const watchStudyCycle = watch('studyCycle');
  const watchStudyLevel = watch('studyLevel');
  const watchClassOrder = watch('classOrder');

  const watchSchoolYearClassTemplateId = watch('schoolYearClassTemplateId');
  const watchSchoolYearClassId = watch('schoolYearClassId');

  const watchReportCard = watch('reportCard');

  const watchFilterUpToDateRegistration = watch('filterUpToDateRegistration');
  const watchFilterUpToDateDueDate = watch('filterUpToDateDueDate');

  const watchSortBy = watch('sortBy');


  const queryClient = useQueryClient();
  const { isLoading, isError, isSuccess, error, mutate } = useMutation<IResult<ISchoolYearClassReportCard>, Error, ISchoolYearClassReportCard>(
    saveSchoolYearClassReportCard, {
    onSuccess: async (data: IResult<ISchoolYearClassReportCard>) => {
      enqueueSnackbar(t('Operation done !!!'), {
        variant: 'success',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 1000
      });

      setIsSaveLoading(false);
      _setId(data.data.id);
      //setCurrentEntityIdForAction(data.data.id);

      const registrationWithMarks = await getMarks({
        reportCard: watchReportCard, registrationId: 0, schoolYearClassId: watchSchoolYearClassId, 
                schoolYearId: watchSchoolYearId ,
                schoolYearPeriodId: (currentReportCardType === 'period') ? watchSchoolYearPeriodId : 0, 
                schoolYearPeriodExamId: (currentReportCardType === 'exam') ? watchSchoolYearPeriodExamId : 0, 
                schoolYearClassTemplateId: watchSchoolYearClassTemplateId,
                studySection: watchStudySection, studyLanguage: watchStudyLanguage, 
                studyType: watchStudyType, studyLevel: watchStudyLevel, studyCycle: watchStudyCycle, 
                
              } );

      // const registrationWithMarks = await getMarks(watchReportCard, 0, watchSchoolYearClassId, watchSchoolYearId,
      //   (currentReportCardType === 'period') ? watchSchoolYearPeriodId : 0,
      //   (currentReportCardType === 'exam') ? watchSchoolYearPeriodExamId : 0);

      setRegistrations(registrationWithMarks);

      //queryClient.invalidateQueries(['SchoolYearClassReportCard',data.data.id]);
    },
    onError: (err: Error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000
      });
      setIsSaveLoading(false);
    }
  });


  const { data: _data, refetch } = useQuery<IMarkDashboard>(['MarkDashboard', _id], () => retrieveEntity('MarkDashboard', _id),
    { refetchOnWindowFocus: false, enabled: false });

  const { data: enumItems } = useQuery<IEnumerationItem[]>(['EnumerationItems', 'MarkDashboard'], () => getEnumerationItemsByEnumerationCodes
    ([Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_STUDY_SECTION,
      Enum_STUDY_TYPE, Enum_PERSON_GENDER, Enum_STUDY_CYCLE]));


  const [gender, setGender] = React.useState<string>('');

  const [schoolYearPeriods, setSchoolYearPeriods] = useState<ISchoolYearPeriod[]>([]);
  const [schoolYearPeriodExams, setSchoolYearPeriodExams] = useState<ISchoolYearPeriodExam[]>([]);

  const [schoolYearClassTemplates, setSchoolYearClassTemplates] = useState<ISchoolYearClassTemplate[]>([]);
  const [classes, setClasses] = useState<ISchoolYearClass[]>([]);

  const [schoolYearClassCountStudents, setSchoolYearClassCountStudents] = useState<number>(0);

  const [registrations, setRegistrations] = useState<IRegistration[]>([]);
  const [selectedRegistration, setSelectedRegistration] = useState<IRegistration>(defaultRegistration);

  const [schoolYearClassSubjects, setSchoolYearClassSubjects] = useState<ISchoolYearClassSubject[]>([]);


  const [schoolYearTemplateSubjectExamSchedules, setSchoolYearTemplateSubjectExamSchedules] = useState<ISchoolYearTemplateSubjectExamSchedule[]>([]);

  const [subjectExamSchedulesByStatus, setSubjectExamSchedulesByStatus] = useState<{ label: string, value: number }[]>([]);

  const [registrationMarksForTypeAppreciations, setRegistrationMarksForTypeAppreciations] = useState<{
    byAppreciations: { label: string, value: number }[],
    bySuccessStatus: { label: string, value: number }[],
    top10Students: IRegistrationTypeMark[], bottom10Students: IRegistrationTypeMark[]
  }>({
    byAppreciations: [], bySuccessStatus: [], top10Students: [], bottom10Students: []
  });

  // const [currentLibrary, setCurrentLibrary] = useState<IExternalLibrary>(defaultExternalLibrary);
  // const [currentLibraryClass, setCurrentLibraryClass] = useState<IExternalLibraryClass>(defaultExternalLibraryClass);

  // const [dbmsType, setDbmsType] = useState<string>('');

  // const [applicationQueryIndex, setApplicationQueryIndex] = useState<number>(-1);
  // const [applicationQueryParameterIndex, setRegistrationMarkIndex] = useState<number>(-1);

  // const [openAppQueryParamDialog, setOpenAppQueryParamDialog] = useState<boolean>(false);

  const getInputAdornmentQueryParameter = (row: IRegistrationMark, cellId: keyof IRegistrationMark) => ({

    toolTip: `${t('Description')} ...`,
    icon: MoreVertIcon,
    onClickIcon: (event: any, index: number, row: IRegistrationMark) => {
      //setRegistrationMarkIndex(index);

      //const {parameterName, parameterDataType } = row;


      //setOpenAppQueryParamDialog(true);
    }
  })



  useEffect(() => {

    const subscription = watch(async (value, { name, type }) => {

      if (name?.startsWith('registrationMarks') && name?.endsWith('markExist')) {
        debouncedMark();
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const debouncedMark = useRef(
    debounce(() => {


      // const marks = getValues().registrationMarks.map( x => x.mark).sort((a,b)=>b-a) ; // desc.
      // const markSet = new Set(marks);
      // const len = getValues().registrationMarks.length;

      // markSet.forEach(function(mark) {

      //   for(var i=0; i< len; i++) {
      //     const registrationMark = getValues().registrationMarks[i];

      //     if(registrationMark.mark !== mark) continue;

      //     const calcRank = getValues().registrationMarks.filter( x => x.markExist && x.mark > mark).length;

      //     (refUpdateRegistrationMark.current??emptyFunc)(i, {
      //       ...registrationMark, 
      //       mark: registrationMark.markExist? mark : 0,
      //       rank: registrationMark.markExist? calcRank+1 : 0
      //     });   
      //   }

      // });


    }, 300)
  ).current;

  const getDataTypesList = (row: IRegistrationMark, cellId: keyof IRegistrationMark,
    opts: { value: string, name: string }[]) => {

    //const {requestClassEnumerationCode} = row;

    return [
      { value: 'string', name: t('Text') }, { value: 'integer', name: t('Integer') }, { value: 'decimal', name: t('Decimal') },
      { value: 'float', name: t('Float') }, { value: 'date', name: t('Date') }, { value: 'boolean', name: t('Boolean') },
      { value: 'time', name: t('Time') },
    ]; // getAsOptions(refEnumItems.current ?? [],Enum_REPORTFIELD_DATA_FORMAT);
  }

  const textDecorationMarkExclude = (row: IRegistration, index: number): string => {

    return row.isMarkExcluded ? 'line-through' : '';
  }

  const [openRegistrationMarkForm, setOpenRegistrationMarkForm] = useState<boolean>(false);

  const registrationRowActionIcon = (registration: IRegistration): ActionIconTableRow<any, IRegistration> => {

    const res: ActionIconTableRow<any, IRegistration> = {
      toolTip: 'more',
      icon: MoreIcon,
      hasAction: true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
      isActionExecuting: true,
      onRowClickIcon: (event: any, index: number, row: IRegistration) => {

        const registration = registrations.find(r => r.id === row.id);
        if (isFalsy(registration)) return;

        setOpenRegistrationMarkForm(true);
        setSelectedRegistration(registration);

        //console.log(registrations[index].subjects);
      }
    }
    return res;
  }

  const handleStudentDoubleClick = (event: React.MouseEvent<unknown>, row: IRegistration) => {
    const roleEntityStudent = roleEntities.find(re => re.entityName === 'Student');
    if (isFalsy(roleEntityStudent)) return;

    navigate(`/student/${row.id}`);
  }

  const handleClickOpenSchoolYearClass = (event: any) => {
    setOpenClassFilter(true);
  }


  const [currentReportCardType, setCurrentReportCardType] = useState<ReportCardType>('period');

  const handleChangeReportCardType = (event: React.MouseEvent<HTMLElement>, type: ReportCardType) => {
    if (isFalsy(type))
      return;

    setCurrentReportCardType(type);

    if (type === 'year') {
      setValue('schoolYearPeriodId', 0);
      setValue('schoolYearPeriodExamId', 0);
    } else if (type === 'period') {
      setValue('schoolYearPeriodExamId', 0);
    }
  }


  useEffect(() => {
    async function _() {

      if (watchSchoolYearId <= 0) return;

      const schoolYear = schoolYears.find(x => x.id === watchSchoolYearId);
      if (isFalsy(schoolYear)) return;

      setValue('schoolYearStartDate', schoolYear.startDate);
      setValue('schoolYearEndDate', schoolYear.endDate);

      const periods = await schoolYearServiceRef.current.getSchoolYearPeriods(watchSchoolYearId);
      setSchoolYearPeriods([...periods]);

      const exams = await schoolYearServiceRef.current.getSchoolYearPeriodExams(watchSchoolYearId);
      setSchoolYearPeriodExams([...exams]);

      if (currentReportCardType === 'period' && periods.length > 0)
        setValue('schoolYearPeriodId', periods[0].id);
      else if (currentReportCardType === 'exam' && periods.length > 0)
        setValue('schoolYearPeriodExamId', exams[0].id);

    }

    _();

  }, [watchSchoolYearId]);


  useEffect(() => {
    async function _() {

      const arr = await schoolYearServiceRef.current.getSchoolYearClassTemplates(watchSchoolYearId, false, '', '');
      setSchoolYearClassTemplates(arr);
    }

    _();

  }, [watchSchoolYearId]);


  const [countReportCard, setCountReportCard] = useState<number>(1);
  useEffect(() => {
    async function _() {
      // const schoolYear = (schoolYears || []).find(sy => sy.id === watchSchoolYearId);
      // const year = isFalsy(schoolYear) ? 0 : schoolYear.year;
      // if(schoolYearId > 0 && schoolYearClassTemplateId > 0) {
      //   const schoolYearClassTemplate = schoolYearClassTemplates.find(t => t.id === schoolYearClassTemplateId);
      //   if(isFalsy(schoolYearClassTemplate)) return;
      //   setCountReportCard(schoolYearClassTemplate!.countReportCard);

      //   if(schoolYearClassTemplate!.countReportCard === 1) 
      //     setValue('reportCard', schoolYearClassTemplate!.minReportCard);        
      // }    

      const arr = await schoolYearServiceRef.current.getSchoolYearClasses({
        schoolYearId: watchSchoolYearId,
        name: '',
        studyType: '',
        studySection: '',
        studyLanguage: '',
        studyLevel: ''
      });

      setClasses(arr);
    }

    _();
  }, [watchSchoolYearId]);


  const [defaultSuccessMark, setDefaultSuccessMark] = useState<number>(10);
  const [firstMarkStudentName, setFirstMarkStudentName] = useState<string>('');
  const [lastMarkStudentName, setLastMarkStudentName] = useState<string>('');
  const [firstMark, setFirstMark] = useState<number>(19.9);
  const [lastMark, setLastMark] = useState<number>(0.1);
  const [avgMark, setAvgMark] = useState<number>(10);




  const loadRegistrationMarks = async () => {

  }

  useEffect(() => {
    async function _() {

      if (watchSchoolYearClassId <= 0 || watchSchoolYearClassTemplateId <= 0 || isFalsy(watchReportCard)) {
        setRegistrations([]);
        return;
      }

      const schoolYearClassTemplate = schoolYearClassTemplates.find(x => x.id === watchSchoolYearClassTemplateId);
      if (isFalsy(schoolYearClassTemplate)) {
        setRegistrations([]);
        return;
      }

      if (currentReportCardType === 'exam' && watchSchoolYearPeriodExamId <= 0) {
        setRegistrations([]);
        return;
      }

      if (currentReportCardType === 'period' && watchSchoolYearPeriodId <= 0) {
        setRegistrations([]);
        return;
      }

      setDefaultSuccessMark(schoolYearClassTemplate.defaultSuccessMark);

      const schoolYearClass = classes.find(c => c.id === watchSchoolYearClassId);
      setSchoolYearClassCountStudents(isFalsy(schoolYearClass) ? 0 : schoolYearClass.countStudents);

      const registrationWithMarks = await getMarks({
        reportCard: watchReportCard, registrationId: 0, schoolYearClassId: watchSchoolYearClassId, 
                schoolYearId: watchSchoolYearId ,
                schoolYearPeriodId: (currentReportCardType === 'period') ? watchSchoolYearPeriodId : 0, 
                schoolYearPeriodExamId: (currentReportCardType === 'exam') ? watchSchoolYearPeriodExamId : 0, 
                schoolYearClassTemplateId: watchSchoolYearClassTemplateId,
                studySection: watchStudySection, studyLanguage: watchStudyLanguage, 
                studyType: watchStudyType, studyLevel: watchStudyLevel, studyCycle: watchStudyCycle
              } );

      const marks = registrationWithMarks.map(x => x.mark).sort((a, b) => b - a); // desc.
      const markSet = new Set(marks);
      const len = registrationWithMarks.length;

      markSet.forEach(function (mark) {
        for (var i = 0; i < len; i++) {
          const registration = registrationWithMarks[i];

          if (registration.mark !== mark) continue;

          const calcRank = registrationWithMarks.filter(x => x.mark > mark).length;
          registration.rank = calcRank + 1;
        }
      });

      setRegistrations(registrationWithMarks);

      const tmpMarks = registrationWithMarks.filter(x => !x.isMarkExcluded).map(x => x.mark);

      const max = (tmpMarks.length > 0) ? Math.max(...tmpMarks) : 0;
      const min = (tmpMarks.length > 0) ? Math.min(...tmpMarks) : 0;
      const sum = (tmpMarks.length > 0) ? tmpMarks.reduce((acc, curr) => acc + curr, 0) : 0;

      const avg = tmpMarks.length > 0 ? sum / tmpMarks.length : 0;

      setFirstMark(max);
      setLastMark(min);
      setAvgMark(avg);

      const firstStudent = registrationWithMarks.find(s => s.mark === max);
      const lastStudent = registrationWithMarks.find(s => s.mark === min);

      setFirstMarkStudentName(isFalsy(firstStudent) ? '' :
        getPhysicalPersonNameLg(firstStudent.firstName, firstStudent.lastName, lg));

      setLastMarkStudentName(isFalsy(lastStudent) ? '' :
        getPhysicalPersonNameLg(lastStudent.firstName, lastStudent.lastName, lg));

    }

    _();


  }, [watchSchoolYearClassId, watchReportCard, watchSchoolYearId, watchSchoolYearPeriodId,
    watchSchoolYearPeriodExamId, watchSchoolYearClassTemplateId,
    classes, currentReportCardType//, getMarks, getPhysicalPersonNameLg, lg,schoolYearClassTemplates,

  ]);



  const debouncedFilterChange = useRef(
    debounce(async (reportCard: string, reportCardType,
      schoolYearId: number, schoolYearPeriodId: number, schoolYearPeriodExamId: number, studyType: string, studySection: string,
      studyLanguage: string, studyCycle: string, studyLevel: string, classOrder: number,
      schoolYearClassTemplateId: number, schoolYearClassId: number) => {

      const arr = await schoolYearServiceRef.current.getSchoolYearClassSubjectExamSchedulesByStatus(
        schoolYearId, schoolYearPeriodId, schoolYearPeriodExamId, studyType, studySection,
        studyLanguage, studyCycle, studyLevel, classOrder, schoolYearClassTemplateId, schoolYearClassId);

      setSubjectExamSchedulesByStatus(arr);

      const obj = await registrationMarkServiceRef.current.getRegistrationMarksForTypeAppreciations(
        reportCard, reportCardType,
        schoolYearId, schoolYearPeriodId, schoolYearPeriodExamId, studyType, studySection,
        studyLanguage, studyCycle, studyLevel, classOrder, schoolYearClassTemplateId, schoolYearClassId
      );

      setRegistrationMarksForTypeAppreciations(obj);

    }, 1000)
  ).current;

  useEffect(() => {
    async function _() {
      debouncedFilterChange(watchReportCard, currentReportCardType, watchSchoolYearId, watchSchoolYearPeriodId, watchSchoolYearPeriodExamId, watchStudyType,
        watchStudySection, watchStudyLanguage, watchStudyCycle, watchStudyLevel, watchClassOrder,
        watchSchoolYearClassTemplateId, watchSchoolYearClassId);
    }
    _();
  }, [watchReportCard, currentReportCardType, watchSchoolYearId, watchSchoolYearPeriodId, watchSchoolYearPeriodExamId, watchStudyType,
    watchStudySection, watchStudyLanguage, watchStudyCycle, watchStudyLevel, watchClassOrder,
    watchSchoolYearClassTemplateId, watchSchoolYearClassId, debouncedFilterChange]);


  useEffect(() => {
    if (!schoolYearPeriodExams.some(x => x.id === watchSchoolYearPeriodExamId && x.schoolYearPeriodId === watchSchoolYearPeriodId))
      setValue('schoolYearPeriodExamId', 0);
  }, [watchSchoolYearPeriodId]);

  useEffect(() => {
    if (!classes.some(x => x.id === watchSchoolYearClassId && x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId))
      setValue('schoolYearClassId', 0);
  }, [watchSchoolYearClassTemplateId]);


  const refEnumItems = useRef<IEnumerationItem[]>();
  useEffect(() => {
    refEnumItems.current = enumItems;
    const studyTypes = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_TYPE);
    if (studyTypes.length === 1)
      setValue('studyType', studyTypes[0].code);

    const studyLanguages = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_LANGUAGE);
    if (studyLanguages.length > 0)
      setValue('reportCard', studyLanguages[0].code);

  }, [enumItems])

  useEffect(() => {
    setCurrentFormNameAtom(`${t('Dashboard')} - ${t('Mark')}`);
    setCurrentBasicTextFilterProps(defaultBasicTextFilterProps);
  }, []);

  useEffect(() => {
    if (getValues().schoolYearId <= 0)
      setValue('schoolYearId', applicationSetup.currentSchoolYearId);
  }, [applicationSetup.currentSchoolYearId, getValues, setValue]);



  /********** This use effect call retrieve data wich will call refetch and _data will be updated. 
and the new useEffect will take place ********************/
  useEffect(() => {
    // setCurrentFormName(t('Billing'));        

    // if(_id > 0)
    //   retrieveData('ApplicationQuery',_id, refetch);  
  }, [_id]);


  useEffect(() => {

    // if(_data && _data.id > 0) {
    //   reset(_data);
    // }
  }, [_data]);


  const newData = async (event: MouseEvent<HTMLButtonElement>) => {
    _setId(0);

    const studyLanguages = (enumItems || []).filter(x => x.enumerationCode === Enum_STUDY_LANGUAGE);

    reset({
      ...defaultSchoolYearClassSubjectMark,
      schoolYearId: applicationSetup.currentSchoolYearId,
      reportCard: studyLanguages.length > 0 ? studyLanguages[0].code : 'Fr'
    });
    setRegistrations([]);
  }


  const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
    openEntityActionDrawer('ApplicationQuery', _id);
  }

  const printData = async (event: MouseEvent<HTMLButtonElement>) => {
    const entityName = (currentReportCardType === 'year') ? 'AnnualMarkDashboard' :
      (currentReportCardType === 'period') ? 'PeriodMarkDashboard' :
        (currentReportCardType === 'exam') ? 'ExamMarkDashboard' : '';

    openEntityPrintDrawerEx(entityName, 0,
      [{ name: 'ReportCard', value: watchReportCard, dateValue: new Date() },
      { name: 'ReportCardType', value: currentReportCardType, dateValue: new Date() },

      { name: 'RegistrationId', value: 0, dateValue: new Date() },

      { name: 'SchoolYearId', value: watchSchoolYearId, dateValue: new Date() },
      { name: 'SchoolYearClassId', value: watchSchoolYearClassId, dateValue: new Date() },
      { name: 'SchoolYearPeriodId', value: watchSchoolYearPeriodId, dateValue: new Date() },
      { name: 'SchoolYearPeriodExamId', value: watchSchoolYearPeriodExamId, dateValue: new Date() },
      { name: 'SchoolYearClassTemplateId', value: watchSchoolYearClassTemplateId, dateValue: new Date() },
      { name: 'StudySection', value: watchStudySection, dateValue: new Date() },
      { name: 'StudyLanguage', value: watchStudyLanguage, dateValue: new Date() },
      { name: 'StudyCycle', value: watchStudyCycle, dateValue: new Date() },
      { name: 'StudyLevel', value: watchStudyLevel, dateValue: new Date() },
      { name: 'StudyType', value: watchStudyType, dateValue: new Date() },
      { name: 'FilterUpToDateRegistration', value: watchFilterUpToDateRegistration, dateValue: new Date() },
      { name: 'FilterUpToDateDueDate', value: String(watchFilterUpToDateDueDate), dateValue: watchFilterUpToDateDueDate },

      { name: 'SortBy', value: String(watchSortBy), dateValue: new Date() }
       
      ]);
  }

  const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retrieveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
  }

  return (
    <FormProvider {...methods} >
      <Box sx={{ mx: 0.1 }}>
        <Grid container rowSpacing={1.5} columnSpacing={1.5}>
          <Grid item xs={12} md={5} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
            <Stack flexDirection='column'  >

              <Box sx={{ mt: 1, width: '100%' }} >

                <Button id='btnNew' onClick={newData} sx={{ display: 'none' }} />

                <Button id='btnAction' onClick={actionData} sx={{ display: 'none' }} />
                <Button id='btnAfterAction' onClick={afterAction} sx={{ display: 'none' }} />

                <Button id='btnPrint' onClick={printData} sx={{ display: 'none' }} />

                {openClassFilter && <FormDialog open={openClassFilter} maxWidth='md'
                  okText={''} cancelText={t('Cancel')} title={t('Classes')} onCancel={() => { setOpenClassFilter(false); }}
                  onClose={() => { setOpenClassFilter(false); }} onOk={() => { }}  >
                  <BasicTextFilterForm<ISchoolYearClass> {...basicFilterSchoolYearClass} />
                </FormDialog>}

              </Box>
              <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t('Exam')} ...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <ToggleButtonGroup size="small"
                  color="primary"
                  value={currentReportCardType}
                  exclusive
                  onChange={(event, newType) => handleChangeReportCardType(event, newType)}
                  aria-label="Small sizes" fullWidth
                >
                  <ToggleButton value='exam'>{t('Exam')}</ToggleButton>
                  <ToggleButton value='period'>{t('Period')}</ToggleButton>
                  <ToggleButton value='year'>{t('Year')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='schoolYearId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('School year')} inputProps={{ readOnly: false }} focused >
                      {schoolYears && schoolYears.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller control={control}
                  name='schoolYearStartDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Start date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />

                <Controller control={control}
                  name='schoolYearEndDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('End date')}
                      onChange={onChange} disableOpenPicker readOnly
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(25% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(25% - 8px)'}} />}
                    />)}
                />
              </Box>
              {currentReportCardType !== 'year' && <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='schoolYearPeriodId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('Period')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value={0}>{':::'}</MenuItem>
                      {schoolYearPeriods && schoolYearPeriods.map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                {currentReportCardType === 'exam' && <Controller name='schoolYearPeriodExamId' control={control}

                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="schoolYearId"
                      label={t('Exam')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value={0}>{':::'}</MenuItem>
                      {schoolYearPeriodExams && schoolYearPeriodExams.filter(x => x.schoolYearPeriodId === watchSchoolYearPeriodId).map(
                        (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />}
              </Box>}
              <Box sx={{ mt: 0.25, mb: 0.25, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t('Class')} ...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >

                <Controller name='studySection' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studySection"
                      label={t('Study section')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_SECTION).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='studyLanguage' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyLanguage"
                      label={t('Study language')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='studyCycle' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyCycle"
                      label={t('Cycle')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_CYCLE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='studyLevel' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyLevel"
                      label={t('Study level')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_LEVEL).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />

              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='studyType' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="studyType"
                      label={t('Study type')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e =>
                        e.enumerationCode === Enum_STUDY_TYPE).map(
                          (x, idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='classOrder' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="classOrder"
                      label={t('Order')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value={0}>{':::'}</MenuItem>
                      {Array.from({ length: 17 }, (_, i) => i + 1).map(
                        (x, idx) => <MenuItem key={x} value={x}>{x}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Controller name='schoolYearClassTemplateId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="schoolYearClassTemplateId"
                      label={`${t('Class')} - ${t('Study level')}`} inputProps={{ readOnly: false }} focused >
                      <MenuItem value={0}>{':::'}</MenuItem>
                      {schoolYearClassTemplates && schoolYearClassTemplates
                        .filter(x => x.studyLanguage.includes(watchStudyLanguage) && x.studySection.includes(watchStudySection) &&
                          x.studyCycle.includes(watchStudyCycle) && x.studyLevel.includes(watchStudyLevel) &&
                          (x.classTemplateLevel === watchClassOrder || watchClassOrder <= 0))
                        .map(
                          (x, idx) => <MenuItem key={x.id} value={x.id}>{x.classTemplateName}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='schoolYearClassId' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="schoolYearClassId"
                      label={`${t('Class')} - `} inputProps={{ readOnly: false }} focused >
                      <MenuItem value={0}>{':::'}</MenuItem>
                      {classes && classes
                        .filter(x => x.studyLanguage.includes(watchStudyLanguage) && x.studySection.includes(watchStudySection) &&
                          x.studyCycle.includes(watchStudyCycle) && x.studyLevel.includes(watchStudyLevel) &&
                          (x.classOrder === watchClassOrder || watchClassOrder <= 0) &&
                          (x.schoolYearClassTemplateId === watchSchoolYearClassTemplateId || watchSchoolYearClassTemplateId <= 0))
                        .map(
                          (x, idx) => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
                <Controller name='reportCard' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(33% - 8px)' }} id="reportCard"
                      label={t('Report card')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      {enumItems && enumItems.filter(e => e.enumerationCode === Enum_STUDY_LANGUAGE).map(
                        (e, ix) => <MenuItem key={e.code} value={e.code}>{e.name}</MenuItem>)
                      }
                    </TextField>
                  )}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Filter'))}...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 0.25, width: '100%' }} >
                <FormControlLabel sx={{ width: 'calc(70% - 8px)' }}
                  label={`${t('Only up to date student ?')}`}
                  control={
                    <Controller
                      name='filterUpToDateRegistration'
                      control={control}
                      render={({ field: { value, onChange, ...props } }) => <Checkbox {...props} checked={value} onChange={onChange} />}
                    />} />
                {watchFilterUpToDateRegistration && <Controller control={control}
                  name='filterUpToDateDueDate'
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DatePicker label={t('Due date')}                      
                      onChange={onChange}
                      value={new Date(value)}
                      slotProps={{ textField: { sx: { width: 'calc(30% - 8px)' } } }}
                    //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                    />)}
                />}
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <Typography variant="h6" id="tableTitle" color="primary" noWrap sx={{ ...typographyGroupBoxStyling }}>
                  {`${t(('Sort'))}...`}
                </Typography>
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >                
                <Controller name='sortBy' control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextField select onChange={onChange} value={value} sx={{ width: 'calc(50% - 8px)' }} id="sortBy"
                      label={t('Sort by')} inputProps={{ readOnly: false }} focused >
                      <MenuItem value=''>{':::'}</MenuItem>
                      <MenuItem value='CurrentRank'>{t('Rank')}</MenuItem>
                      <MenuItem value='StudentNameFR'>{`${t('Name')} FR`}</MenuItem>
                      <MenuItem value='StudentNameEN'>{`${t('Name')} EN`}</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
              

              <Box sx={{ mt: 4, width: '100%' }} >
                <WidgetPieChart title={`${t('Status of marks per subject')}`} subheader=''
                  chartData={[{
                    label: `⏳ ${t('Not saved')}`,
                    value: sum((subjectExamSchedulesByStatus || []).filter(r => r.label === '00').map(({ value }) => value))
                  },
                  {
                    label: `⏳ ${t('Not validated')}`,
                    value: sum((subjectExamSchedulesByStatus || []).filter(r => r.label === '95').map(({ value }) => value))
                  },
                  {
                    label: `✅ ${t('Validated')}`,
                    value: sum((subjectExamSchedulesByStatus || []).filter(r => r.label === '10').map(({ value }) => value))
                  },
                  {
                    label: `❌ ${t('Cancelled')} - ${t('Other')}`,
                    value: sum((subjectExamSchedulesByStatus || []).filter(r => !['00', '95', '10'].includes(r.label)).map(({ value }) => value))
                  },
                  ]}
                  chartColors={[
                    theme.palette.warning.main,
                    theme.palette.info.main,
                    theme.palette.success.main,
                    theme.palette.error.main,
                  ]} />

              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7} component={Paper} >
            <Stack flexDirection='column'>
              <Box sx={{
                mt: 1, width: '100%',
                display: 'grid', gap: 1.5, // spacing between items (adjust as needed)
                gridTemplateColumns: {
                  xs: 'repeat(1, 1fr)',  // 3 per row on extra-small screens (sx)
                  sm: 'repeat(1, 1fr)',  // 4 per row on small screens (sm)
                  md: 'repeat(2, 1fr)',  // 5 per row on medium screens (md) and up
                },
              }} >
                <WidgetPieChart title={`${t('Marks')} - ${t('Appreciation')}`} subheader=''
                  chartData={registrationMarksForTypeAppreciations.byAppreciations}
                  chartColors={registrationMarksForTypeAppreciations.byAppreciations.map(({ label }) => generateDistinctColorFromName(label))} />
                <WidgetPieChart title={`${t('Statistical analysis of result')}`} subheader=''
                  chartData={[{
                    label: `✅ ${t('Success')}`,
                    value: sum(registrationMarksForTypeAppreciations.bySuccessStatus.filter(({ label }) => label === 'successed').map(({ value }) => value))
                  },

                  {
                    label: `❌ ${t('Failled')}`,
                    value: sum(registrationMarksForTypeAppreciations.bySuccessStatus.filter(({ label }) => label === 'failled').map(({ value }) => value))
                  },
                  ]}
                  chartColors={[
                    theme.palette.success.main,
                    theme.palette.warning.main,
                  ]} />
              </Box>

              {(registrations || []).length > 0 && <Box sx={{ mt: 1, width: '100%' }} >
                <Stack flexDirection='row'>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', padding: 2, width: '50%' }} >
                    <Avatar sx={{ backgroundColor: theme.palette.success.main }}>{t('F_')}</Avatar>
                    <Stack flexDirection='column'>
                      <Typography variant="h6" id="tableTitle" color={theme.palette.success.main} noWrap>
                        {t('Best in the class')}
                      </Typography>
                      <Typography variant="body2" id="tableTitle" color="primary" noWrap sx={{ marginLeft: 'auto' }}>
                        {firstMarkStudentName}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" id="tableTitle" color={theme.palette.success.main} noWrap >
                      {firstMark.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', padding: 2, width: '50%' }} >
                    <Avatar sx={{ backgroundColor: theme.palette.warning.main }}>{t('L_')}</Avatar>
                    <Stack flexDirection='column'>
                      <Typography variant="h6" id="tableTitle" color={theme.palette.warning.main} noWrap>
                        {t('Last in the class')}
                      </Typography>
                      <Typography variant="body2" id="tableTitle" color="primary" noWrap sx={{ marginLeft: 'auto' }}>
                        {lastMarkStudentName}
                      </Typography>
                    </Stack>
                    <Typography variant="h6" id="tableTitle" color={theme.palette.warning.main} noWrap >
                      {lastMark.toFixed(2)}
                    </Typography>
                  </Box>
                </Stack>

              </Box>}

              <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<IRegistrationTypeMark>
                  rows={registrationMarksForTypeAppreciations.top10Students || []}
                  headCells={[

                    { id: 'schoolYearClassName', label: t('Class'), display: true, type: 'string', width: 25 },

                    {
                      id: lg.includes('en') ? 'studentNameEN' : 'studentNameFR', label: t('Name'),
                      display: true, type: 'string', width: 25
                    },

                    { id: 'averageMark', label: `${t('Mark')}`, display: true, type: 'numeric', width: 14, decimalScale: 2 },
                    { id: 'rank', label: `${t('Rank')}`, display: true, type: 'numeric', width: 9, },

                    { id: 'appreciationName', label: `${t('Appreciation')}`, display: true, type: 'string', width: 14, decimalScale: 2 },
                    { id: 'successed', label: `${t('Success')}`, display: true, type: 'boolean', width: 9, },

                  ]}
                  title={`${t('Top 10')}`} objKey='id'
                  stateSelected={undefined}
                  onRowSelected={undefined} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='averageMark'
                  onRowDoubleClick={undefined}
                  rowActionIcon={undefined} // subjectRowActionIcon
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />
              </Box>
              <Box sx={{ mt: 1, width: '100%' }} >
                <EnhancedTable<IRegistrationTypeMark>
                  rows={registrationMarksForTypeAppreciations.bottom10Students || []}
                  headCells={[

                    { id: 'schoolYearClassName', label: t('Class'), display: true, type: 'string', width: 25 },

                    {
                      id: lg.includes('en') ? 'studentNameEN' : 'studentNameFR', label: t('Name'),
                      display: true, type: 'string', width: 25
                    },

                    { id: 'averageMark', label: `${t('Mark')}`, display: true, type: 'numeric', width: 14, decimalScale: 2 },
                    { id: 'rank', label: `${t('Rank')}`, display: true, type: 'numeric', width: 9, },

                    { id: 'appreciationName', label: `${t('Appreciation')}`, display: true, type: 'string', width: 14, decimalScale: 2 },
                    { id: 'successed', label: `${t('Success')}`, display: true, type: 'boolean', width: 9, },

                  ]}
                  title={`${t('Bottom 10')}`} objKey='id'
                  stateSelected={undefined}
                  onRowSelected={undefined} rowCheckedMode='single'
                  onRowCheckedSelectChange={undefined} order='desc' orderBy='averageMark'
                  onRowDoubleClick={undefined}
                  rowActionIcon={undefined} // subjectRowActionIcon
                  toolbarActions={[
                    // { toolTip: `${t('Add')}...`, onClickIcon: handleRefeshRequestDataDemand ,icon: RefreshOutlinedIcon,  },                      
                  ]}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </FormProvider>
  )
}
